import _ from 'lodash'
import $ from 'jquery'
import Backbone from 'backbone'
import MultiPrincipalChooser from 'scoreboard/components/multi-principal-chooser'
import i18n from 'scoreboard/components/i18n'
import template from './import-export-filter-panel.hbs'
import principal from 'scoreboard/components/principals'
import user from 'scoreboard/components/user'
const MAX_RESULTS = 600
const AUTO_FILTER_SELECTION_THRESHOLD = 10

const optionEquals = function(opt1, opt2) {
	// #28992 handle items that are matched by label, not by id
	// (ex. scheduled export format)
	if (!opt1 || !opt2) {
		return opt1 === opt2
	}

	if (_.isNaN(opt1.id) || _.isNaN(opt2.id)) {
		return opt1.label === opt2.label && opt1.type === opt2.type
	} else {
		return principal.equals(opt1, opt2)
	}
}

/**
 * A dropdown that provide a single filter using a rich select, used to provide
 * filtering in #21969 & #20687
 */
const ImportExportFilter = MultiPrincipalChooser.extend({

	/**
	 * @param {Object[]} options.items The import/exports available to filter against
	 *
	 * @param {Principal[]} options.includePrincipals the principals to include
	 * all others will not be shown
	 */
	initialize: function(options= {}) {
		this._populatePrincipalGroups(options.currentPrincipals)

		MultiPrincipalChooser.prototype.initialize.apply(this, arguments)
		this.items = options.items || []
	},

	_populatePrincipalGroups: function(principals) {
		_.forEach(principals, currPrincipal => {
			if (currPrincipal.type === 'group') {
				if (currPrincipal.id > 0) {
					currPrincipal.group = i18n.get('common.owners')
				} else {
					currPrincipal.group = 'headerless'
				}
			}
			if (currPrincipal.type === 'user') {
				currPrincipal.group = i18n.get('common.owners')
			}
		})
	},

	draw: function(results = [], options) {
		// #31586 We can add the current user because if the current user owns
		// nothing, it'll just get filtered out below
		let resultsContainCurrentUser = !!_.find(results,
				it => principal.equals(it, user.principal))
		if (!resultsContainCurrentUser) results.push(user.principal)

		let includePrincipalsMap = principal.toMap(this.options.includePrincipals)
		results = results.filter(it => includePrincipalsMap.has(principal.hashCode(it)))

		this._populatePrincipalGroups(results)
		let opts = this.items
		//don't duplicate the one that's already selected
		let selected = this._selectedPrincipals()
		if (selected.length) {
			let optsMinusSelected = _.filter(opts, item => !optionEquals(selected[0], item))
			// #30867 If the selected item is no longer an option, inform parent view:
			// Bear in mind that at this point "results" are the real principals here.
			// "opts" represents all the other types of filters. So first, let's see if
			// anything in "opts" matches the selected filter. i.e. Did we remove anything
			// in the above filter operation?
			if (optsMinusSelected.length === opts.length) {
				// No? Then we know that the selected item doesn't match a non-principal filter.
				// Now let's check principals... Is it in our include map?
				if (!_.isEmpty(selected[0]) && !includePrincipalsMap.has(principal.hashCode(selected[0]))) {
					// If not, it doesn't belong in the options list. Emit not found.
					this.trigger('initialSelectionNotFound')
				}
			}
			opts = optsMinusSelected
		}
		results.push(...opts)
		return MultiPrincipalChooser.prototype.draw.call(this, results, options)
	},

	show(options = {}) {
		// We don't need to call back to the server when we show the filter select
		// is clicked.
		if (options instanceof $.Event) {
			return
		}

		MultiPrincipalChooser.prototype.show.apply(this, arguments)
	}

})

let filterCache

const ImportExportFilterPanel = Backbone.View.extend({

	tagName: 'div',

	className: 'panel import-export-filter-panel form',

	/**
	 * The panel holding a filter for import/exports.  Uses a list of import/exports
	 * and builds a filter dropdown that returns matching import/exports.
	 *
	 * @constructs
	 *
	 * @param {Object[]} options.filters An array of objects passed to the
	 * select to build the option dropdown
	 *
	 * @param {Principal[]} [options.owningPrinciples] all principles that own any imports -
	 * either directly or indirectly
	 *
	 * @param {Object} [options.importOrExportIdFromUrl] the ID of the default
	 * import/export to select, as a filter, when the threshold number of
	 * imports/exports is met for an automatic filter
	 */
	initialize(options = {}) {
		this.options = options
		this._includeIndirectOwnership = true
		this._itemIds = []

		this.clear()
		_.each(options.filters || [], this.addFilter.bind(this))
	},

	events: {
		'change [name=includeIndirectOwnership]': 'onChangeIncludeIndirectOwnership',
		'click .clear': 'clearFilter'
	},

	/**
	 * Clears the current list of filters and import/exports this filtering panel
	 * contains
	 */
	clear: function() {
		//map of filter groups whose key is the filter id and values are the set
		//of item ids that match the filter id
		this._itemIdsByTypeAndTerm = {}
		this._itemIds = []
		_.each(this.options.filterTypes || ['user','group'],
			typeId => this._itemIdsByTypeAndTerm[typeId] = new Map())
		this._importIdsByIndirectOwnerTypeAndId = {
			user: new Map(),
			group: new Map()
		}
		this._importIdsByFromGroupUserId = {}
		this._filterIdsByGroup = {}
		this._filters = []
	},

	/**
	 * Adds an import/export to list of searchable items that the filter
	 * searches against
	 *
	 * @param {Integer} item.id the id of the import/export
	 * @param {Object[]} [item.filters] array of objects for the filter options
	 * this import/export
	 * @param {Object[]} item.matches array of objects that contains all the
	 * filter values that the import/export matches against
	 * @param {Principal[]} item.indirectOwners the principals that indirectly own the import
	 * @param {Principal[]} item.usersFromGroups all users that are a member
	 * of one or more owning groups for the item. (Only applicable for imports)
	 */
	addItemFilter: function(item) {
		let {id: itemId, filters, matches, indirectOwners, usersFromGroups} = item
		this._itemIds.push(itemId)

		//store the item id matching the terms
		_.each(matches, matcher => {
			let {groupId, term} = matcher
			if (term) {
				let groupMap = this._itemIdsByTypeAndTerm[groupId]
				let idsMatchingInGroup = groupMap.get(term) || new Set()
				idsMatchingInGroup.add(itemId)
				groupMap.set(term, idsMatchingInGroup)
			}
		})

		_.each(indirectOwners || [], ({type, id}) => {
			let indirectOwnersForTypeById = this._importIdsByIndirectOwnerTypeAndId[type]
			let importIdsForOwnerTypeAndId = indirectOwnersForTypeById.get(id) || new Set()
			importIdsForOwnerTypeAndId.add(itemId)
			indirectOwnersForTypeById.set(id, importIdsForOwnerTypeAndId)
		})

		_.each(usersFromGroups || [], ({id}) => {
			this._importIdsByFromGroupUserId[id] =
				this._importIdsByFromGroupUserId[id] || []
			this._importIdsByFromGroupUserId[id].push(itemId)
		})

		if (filters) {
			_.each(filters, f => {
				// #32813 The filter might have been changed with respect to
				// non-equality properties. (e.x. import name.)
				// Reassign our copy to the latest version.
				if (optionEquals(f, this._lastItem)) {
					this._lastItem = f
				}
				this.addFilter(f)
			})
		}
	},

	/**
	 * Adds a filter option to the list of options to filter against
	 * @param {Object} filter the object to pass to the select to build a option
	 * in a filtering select
	 */
	addFilter: function(filter) {
		if (filter.id === 'rest') {
			filter.label = _.upperCase(filter.label)
		}
		//add the term to filter dropdown set if not already present
		let groupId = filter.group || 'headless'
		let set = this._filterIdsByGroup[groupId] || new Set()
		if (!set.has(filter.id)) {
			this._filters.push(filter)
		}
		set.add(filter.id)
		this._filterIdsByGroup[groupId] = set
	},

	render: function(clearingFilter = false) {
		this.filter && this.filter.remove()
		this.$el.empty().append(template(_.extend({
			includeIndirectOwnership: this._includeIndirectOwnership
		}, this.options)))
		this.delegateEvents()
		let selectedFilter = clearingFilter ? undefined : this._getSelectedFilter()
		this.filter = new ImportExportFilter({
			el: this.$('.import-export-filter-container'),
			cache: filterCache,
			placeholder: i18n.get('datasets.filters.choose'),
			multiple: false,
			autofocus: false,
			groupBy: 'group',
			items: this._filters,
			currentPrincipals: selectedFilter ? [selectedFilter] : undefined,
			includePrincipals: this.options.owningPrinciples
		}).render()
		this.listenTo(this.filter, 'select', this._emitMatched.bind(this))
		this.filter.show({open: false})
		this.resize()
		this._emitMatched(selectedFilter)
		this.listenTo(this.filter, 'initialSelectionNotFound', () => {
			// #32436 If the filter that wasn't found was the default filter,
			// don't re-render, (i.e. auto-select it), That would just cause
			// infinite recursion.
			let defaultFilter = this._getDefaultFilter()
			if (!optionEquals(selectedFilter, defaultFilter)) {
				this._lastItem = defaultFilter
				this.render()
			}
		})
		return this
	},

	_getSelectedFilter: function() {
		if (this._lastItem) return this._lastItem
		return this._getDefaultFilter()
	},

	// Get the default filter if required i.e threshold import/export count met
	// This will be the current user, if appropriate, but we might also need to
	// filter for a specific import/export 1) if there is an ID for one in the URL
	// and 2) if that import/export would not display because of too many results
	// or because the current user filter would filter it out
	_getDefaultFilter: function() {
		if (this._itemIds.length >= AUTO_FILTER_SELECTION_THRESHOLD) {
			if (this.options.importOrExportIdFromUrl) {
				const byNameFilter = this._filters.find(it => it.type === 'label' &&
					it.id === this.options.importOrExportIdFromUrl)
				if (!byNameFilter) return user.principal

				const matchesForCurrUser = this.getMatches(user.principal)
				const matchesByName = this.getMatches(byNameFilter)

				const userFilterExcludesByNameFilterResults =
					!_.every([...matchesByName],
						it => matchesForCurrUser.has(it))
				const userFilterReturnsTooManyResults
					= matchesForCurrUser.length > MAX_RESULTS
				const userFilterIsUnsuitable = userFilterExcludesByNameFilterResults ||
					userFilterReturnsTooManyResults

				return userFilterIsUnsuitable ? byNameFilter : user.principal
			} else {
				return user.principal
			}
		}
	},

	/**
	 * Get the items that match the selected filter via a single lookup on type and ID
	 * if {_includeIndirectOwnership} is false, or by combining that lookup with the results of
	 * a similar lookup on {_importIdsByIndirectOwnerTypeAndId} - also by type and ID.
	 */
	getMatches(item) {
		if (item) {
			var matchingOptionGroup = this._itemIdsByTypeAndTerm[item.type] || {}
			var directlyMatchedIds = matchingOptionGroup.get(item.id) || []
			if (this._includeIndirectOwnership && item.type === 'group') {
				let indirectlyMatchedOptionsGroup = this._importIdsByIndirectOwnerTypeAndId[item.type] || {}
				let indirectlyMatchedIds = indirectlyMatchedOptionsGroup.get(item.id) || []
				return new Set([...directlyMatchedIds, ...indirectlyMatchedIds])
			} else if (this._includeIndirectOwnership && item.type === 'user') {
				// #36200 We handle matches, from group memberships, differently for
				// users than for groups. With groups we care about transitive
				// memberships; with users we do not. We are only interested in what
				// groups the user is _directly_ a member of, and we do not consider any
				// groups that might be members of those groups in turn. Hence we will
				// be looking at _importIdsByFromGroupUserId, (direct owning group
				// members), rather than _importIdsByIndirectOwnerTypeAndId
				const userIdsFromAnyOwningGroup =
					this._importIdsByFromGroupUserId[item.id] || []
				return new Set([
					...directlyMatchedIds, ...userIdsFromAnyOwningGroup
				])
			} else {
				return new Set(directlyMatchedIds)
			}
		}

		return null
	},

	onChangeIncludeIndirectOwnership: function(e) {
		this._includeIndirectOwnership = e.target.checked
		this._emitMatched(this._lastItem)
	},

	_emitMatched: function(item) {
		let itemType = _.result(item, 'type')
		this.$('.include-indirect-ownership')
			.toggleClass('is-not-displayed', itemType != 'user' && itemType != 'group')
		this._lastItem = item
		this.trigger('matches', this.getMatches(item))
		this.resize()
		this.$el.toggleClass('has-filter', !_.isNil(item))
	},

	clearFilter() {
		this._lastItem = undefined
		this.render(true)
		this._emitMatched()
	},

	resize: function() {
		let container = this.$('.import-export-filter-container')
		let panelHeader = this.$('.panel-header').innerWidth()
		let clearButton = this.$('button.clear').width()
		// 80px = extra space so the clear button fits comfortably
		container.css('width', panelHeader - clearButton - 80)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.filter && this.filter.remove()
		filterCache = null
	}

})

export default ImportExportFilterPanel
export { MAX_RESULTS }
ImportExportFilterPanel.__filename__ = 'admin/imports/import-export-filter-panel.js'
